import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Loader2, AlertCircle, AlertTriangle, Circle, Check, Info, RotateCcw, ShieldCheck,
} from 'lucide-react';
import { Button } from '../ui-v2';
import { getDeshacerAlta, marcarDeshacerAlta } from './api';
import { fmtFecha } from './fecha';

/**
 * DeshacerAlta — la sección de un alta «cancelándose» (pieza 5.2).
 *
 * Lista las tareas de deshacer que generó la cancelación y deja marcarlas. MARCAR LO HACE (lo
 * decidió Iban el 16/10): no es una casilla que apunta, es la acción. Por eso cada fila dice, ANTES
 * del clic, qué va a pasar al marcarla. Lo dice el servidor con `efecto`; el texto vive aquí:
 *
 *   · `devuelve_en_inventario`  registra la devolución en Inventario; desmarcar se lo reasigna.
 *   · `revoca_en_la_intranet`   revoca la credencial, y no se desmarca. Y se dice que es un
 *                               REGISTRO: la intranet registra el trabajo, no lo hace; la cuenta
 *                               hay que revocarla en su sistema. Dicho donde se marca, como en la baja.
 *   · `solo_registro`           lo apunta y nada más (un correo enviado no se des-envía).
 *
 * Y cada fila dice QUÉ SE SABE de ella, con la verificación del servidor: una marca que Inventario
 * contradice no es una duda, son dos datos nuestros que no casan, y va en rojo. Una que nadie
 * puede comprobar desde aquí se dice como tal: tratarlas igual prometería lo mismo sobre cosas que
 * no se saben igual.
 *
 * `onDatos` le cuenta al padre lo cargado, porque «Volver a en curso» depende de si hay algo marcado.
 */

const EFECTO = {
  devuelve_en_inventario: 'Marcarla registra la devolución en Inventario. Desmarcarla se lo vuelve a asignar, si nadie lo ha movido.',
  revoca_en_la_intranet: 'Marcarla revoca la credencial, y no se puede desmarcar. Revocar aquí lo deja registrado en la intranet: la cuenta hay que revocarla en su sistema.',
  solo_registro: 'Marcarla sólo lo apunta: la intranet no puede hacerlo ni comprobarlo.',
};

const cuerpoError = (e) => (e && e.response && e.response.data) || {};

export default function DeshacerAlta({ altaId, puedeMarcar, onDatos }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(false);
  const [enVuelo, setEnVuelo] = useState(() => new Set());

  const aplicar = useCallback((r) => {
    const limpio = { tareas: r.tareas || [], verificacion: r.verificacion || null };
    setDatos(limpio);
    if (onDatos) onDatos(limpio);
  }, [onDatos]);

  const cargar = useCallback(async () => {
    setError(false);
    try {
      aplicar(await getDeshacerAlta(altaId));
    } catch (_) {
      setError(true);
    }
  }, [altaId, aplicar]);

  useEffect(() => { cargar(); }, [cargar]);

  async function cambiar(t) {
    if (enVuelo.has(t.id)) return;
    const estado = t.estado === 'completada' ? 'pendiente' : 'completada';
    setEnVuelo((s) => new Set(s).add(t.id));
    try {
      const r = await marcarDeshacerAlta(altaId, t.id, estado);
      aplicar(r);
      // Las notas del servidor dicen lo que NO se pudo hacer, o lo que sigue haciendo falta fuera.
      (r.notas || []).forEach((n) => toast.info(n, { autoClose: 9000 }));
      if (!(r.notas || []).length) {
        toast.success(estado === 'completada' ? `Hecho: ${t.descripcion}` : `Vuelve a pendiente: ${t.descripcion}`);
      }
    } catch (e) {
      toast.error(cuerpoError(e).message || 'No se pudo cambiar la tarea.');
      cargar();
    } finally {
      setEnVuelo((s) => { const n = new Set(s); n.delete(t.id); return n; });
    }
  }

  if (error) {
    return (
      <section className="rounded-v25-lg border border-v25-line bg-white px-4 py-6 text-center text-sm text-red-600">
        <AlertCircle className="mx-auto mb-2 h-5 w-5" />No se pudieron cargar las tareas de deshacer.
        <div><Button variant="outline" className="mt-3" onClick={cargar}>Reintentar</Button></div>
      </section>
    );
  }
  if (!datos) {
    return (
      <section className="flex items-center justify-center gap-2 rounded-v25-lg border border-v25-line bg-white py-6 text-sm text-v25-ink-500">
        <Loader2 className="h-4 w-4 animate-spin" />Cargando lo que hay que deshacer…
      </section>
    );
  }

  const v = datos.verificacion || {};
  const porId = (lista) => new Map((lista || []).map((x) => [Number(x.id), x]));
  const contradice = porId(v.contradicciones);
  const sinVerificar = porId(v.sin_verificar);
  const confirmada = porId(v.confirmadas);
  const declarada = porId(v.declaradas);
  const marcadas = datos.tareas.filter((t) => t.estado === 'completada').length;

  return (
    <section className="rounded-v25-lg border border-v25-waiting-bd bg-white">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-v25-line bg-v25-waiting-bg px-4 py-2.5">
        <RotateCcw className="h-4 w-4 text-v25-waiting-fg" />
        <h2 className="text-sm font-semibold text-v25-ink-900">Deshacer el alta</h2>
        <span className="text-xs tabular-nums text-v25-ink-500">{marcadas} de {datos.tareas.length} hechas</span>
      </div>

      <div className="space-y-1 px-4 py-2.5 text-xs text-v25-ink-700">
        <p>
          Este alta se está cancelando. Estas tareas deshacen lo que ya se hizo para la incorporación.
          Mientras no marques ninguna, puedes volver a «en curso».
        </p>
        {v.vacio && (
          <p className="text-v25-ink-500">No se encontró nada hecho que haya que deshacer.</p>
        )}
        {!!(v.contradicciones || []).length && (
          <p className="flex items-start gap-1.5 text-red-700">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {v.contradicciones.length === 1 ? 'Una tarea marcada no casa' : `${v.contradicciones.length} tareas marcadas no casan`} con
            lo que dice Inventario. Hay que resolverlo antes de seguir.
          </p>
        )}
      </div>

      {datos.tareas.length > 0 && (
        <ul className="divide-y divide-v25-line border-t border-v25-line">
          {datos.tareas.map((t) => {
            const hecha = t.estado === 'completada';
            const ocupada = enVuelo.has(t.id);
            // Una credencial revocada no se des-revoca: el botón no se ofrece.
            const fija = hecha && t.efecto === 'revoca_en_la_intranet';
            const c = contradice.get(Number(t.id));
            const sv = sinVerificar.get(Number(t.id));
            return (
              <li key={t.id} className="flex items-start gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className={`text-sm ${hecha ? 'text-v25-ink-500' : 'text-v25-ink-900'}`}>{t.descripcion}</div>
                  {!hecha && <div className="mt-0.5 text-[11px] text-v25-ink-500">{EFECTO[t.efecto] || EFECTO.solo_registro}</div>}
                  {hecha && (
                    <div className="mt-0.5 text-[11px] text-v25-ink-400">
                      Hecha{t.completada_en ? ` el ${fmtFecha(t.completada_en)}` : ''}.
                      {t.efecto === 'revoca_en_la_intranet' && ' Recuerda: la cuenta hay que revocarla en su sistema.'}
                    </div>
                  )}
                  {c && (
                    <div className="mt-1 flex items-start gap-1 text-[11px] text-red-700">
                      <AlertTriangle className="mt-px h-3 w-3 shrink-0" />{c.dice}
                    </div>
                  )}
                  {sv && (
                    <div className="mt-1 flex items-start gap-1 text-[11px] text-v25-waiting-fg">
                      <Info className="mt-px h-3 w-3 shrink-0" />No se puede confirmar: {sv.motivo}
                    </div>
                  )}
                  {confirmada.has(Number(t.id)) && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-v25-active-fg">
                      <ShieldCheck className="h-3 w-3" />Inventario lo confirma: está devuelto.
                    </div>
                  )}
                  {declarada.has(Number(t.id)) && (
                    <div className="mt-1 text-[11px] text-v25-ink-400">Nadie puede comprobarlo desde la intranet: vale lo que se ha marcado.</div>
                  )}
                </div>
                {puedeMarcar ? (
                  <button
                    type="button"
                    onClick={() => cambiar(t)}
                    disabled={ocupada || fija}
                    title={fija ? 'Una credencial revocada no se puede desmarcar.' : (hecha ? 'Desmarcar' : 'Marcar como hecha')}
                    className={`inline-flex shrink-0 items-center gap-1 rounded-v25-full border px-2.5 py-1 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-60 ${hecha
                      ? 'border-v25-active-bd bg-v25-active-bg text-v25-active-fg'
                      : 'border-v25-info-bd bg-v25-info-bg text-v25-info-fg'}`}
                  >
                    {ocupada ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : hecha ? <Check className="h-3.5 w-3.5" /> : <Circle className="h-3.5 w-3.5" />}
                    {hecha ? 'Hecha' : 'Pendiente'}
                  </button>
                ) : (
                  <span className={`shrink-0 text-xs ${hecha ? 'text-v25-active-fg' : 'text-v25-ink-400'}`}>{hecha ? 'Hecha' : 'Pendiente'}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
